// src/components/common/audio-player/core/usePlaybackState.ts

import { useCallback, useMemo, useState } from "react";

export type PlayRate = 0.75 | 1 | 1.25;

export type PlaybackState = {
  playing: boolean;
  duration: number;
  time: number;
  bufferedEnd: number;
  rate: PlayRate;
};

export type PlaybackControls = {
  setPlaying: (v: boolean) => void;
  setDuration: (v: number) => void;
  setTime: (v: number) => void;
  setBufferedEnd: (v: number) => void;
  setRate: (r: PlayRate) => void;
};

const clampRate = (r: number): PlayRate =>
  r < 0.75 ? 0.75 : r > 1.25 ? 1.25 : (r as PlayRate);

export function usePlaybackState(initialRate: PlayRate = 1) {
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [time, setTime] = useState(0);
  const [bufferedEnd, setBufferedEnd] = useState(0);
  const [rate, _setRate] = useState<PlayRate>(clampRate(initialRate));

  const setRate = useCallback((r: PlayRate) => {
    _setRate(clampRate(r));
  }, []);

  const state: PlaybackState = useMemo(
    () => ({ playing, duration, time, bufferedEnd, rate }),
    [playing, duration, time, bufferedEnd, rate]
  );

  const controls: PlaybackControls = useMemo(
    () => ({
      setPlaying,
      setDuration,
      setTime,
      setBufferedEnd,
      setRate,
    }),
    [setRate]
  );

  return { state, controls };
}
